import { useEffect, useState } from "react";
import { isAxiosError } from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { hasOnlyRole } from "@myapp/contract";
import { useTaskDetail, getTaskTypeLabel, isTaskInProgress } from "../../entities/task";
import { useAuthStore } from "../../features/auth";
import { FieldWrapper, Input, Loader } from "../../shared/ui";
import { PATHS } from "../../app/routes/paths";
import { SuccessDialog } from "../../shared/ui/feedback/SuccessDialog/SuccessDialog";
import { useSuccessDialog } from "../../shared/hooks/useSuccessDialog";
import { CompleteTaskModal } from "../../features/aufgaben/complete";
import { StartTaskModal } from "../../features/aufgaben/start";
import { WorkerTaskPlanOverlay } from "../../widgets/worker-task-plan/ui/WorkerTaskPlanOverlay";
import { TaskHeader } from "./ui/TaskHeader";
import { TaskDurationPills } from "./ui/TaskDurationPills";
import { TaskActions } from "./ui/TaskActions";
import { formatDate, formatDateTime, formatValue, formatAttachments } from "./lib";

export const WorkerTaskPage = () => {
  const navigate = useNavigate();
  const { taskId } = useParams<{ taskId: string }>();
  const id = Number(taskId);

  const user = useAuthStore((state) => state.user);
  const { data, isLoading, isError, error } = useTaskDetail(id);
  const [showPlan, setShowPlan] = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  const [showStart, setShowStart] = useState(false);
  const { showSuccess, dialogProps } = useSuccessDialog();

  useEffect(() => {
    if (isLoading) return;

    if (isError) {
      const status = isAxiosError(error) ? error.response?.status : undefined;
      if (status === 403 || status === 404) {
        navigate(PATHS.workerCalendar, { replace: true });
      }
      return;
    }

    if (data && hasOnlyRole(user?.roles ?? [], 'WORKER') && data.assignedTechnicianId !== Number(user?.id)) {
      navigate(PATHS.workerCalendar, { replace: true });
    }
  }, [data, error, isError, isLoading, navigate, user]);

  if (isLoading || (hasOnlyRole(user?.roles ?? [], 'WORKER') && data?.assignedTechnicianId !== Number(user?.id))) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center h-full text-red-400">
        Aufgabe konnte nicht geladen werden.
      </div>
    );
  }

  const isTimeScheduled = !!data.plannedStart && new Date(data.plannedStart).getUTCHours() !== 0;
  const isInProgress = isTaskInProgress(data);

  return (
    <>
      <div className="flex flex-col h-full">
        <div className="px-4 pt-4">
          <button onClick={() => navigate(-1)} className="text-blue-600 text-sm font-medium">
            ← Zurück
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-4 pt-3">
          <TaskHeader title={formatValue(data.title)} status={data.status} />

          <FieldWrapper label="Datum">
            <Input value={formatDateTime(data.plannedStart)} disabled />
          </FieldWrapper>

          <FieldWrapper label="Abgabefrist">
            <Input value={formatDate(data.deadline)} disabled />
          </FieldWrapper>

          <TaskDurationPills
            variant="planned"
            work={data.plannedWorkDuration ?? 0}
            travel={data.plannedTravelDuration ?? 0}
            departure={data.plannedDepartureDuration ?? null}
          />

          {data.status === "COMPLETED" &&
            data.actualWorkDuration != null &&
            data.actualTravelDuration != null && (
              <TaskDurationPills
                variant="actual"
                work={data.actualWorkDuration}
                travel={data.actualTravelDuration}
                departure={data.actualDepartureDuration ?? null}
              />
            )}

          <FieldWrapper label="Aufgabentyp">
            <Input value={getTaskTypeLabel(data.taskType)} disabled />
          </FieldWrapper>

          <FieldWrapper label="Beschreibung">
            <Input
              value={formatValue(data.description)}
              disabled
              as="textarea"
              inputClassName="!min-h-[100px]"
            />
          </FieldWrapper>

          {data.attachments.length > 0 && (
            <FieldWrapper label="Attachments">
              <Input
                value={formatAttachments(data.attachments)}
                disabled
                as="textarea"
                inputClassName="!min-h-[100px]"
              />
            </FieldWrapper>
          )}
        </div>

        <TaskActions
          isCompleted={data.status === "COMPLETED"}
          isTimeScheduled={isTimeScheduled}
          isInProgress={isInProgress}
          onMarkDone={() => setShowComplete(true)}
          onStart={() => setShowStart(true)}
          onPlan={() => setShowPlan(true)}
        />
      </div>

      {showPlan && (
        <WorkerTaskPlanOverlay
          show={showPlan}
          onClose={() => setShowPlan(false)}
          onSuccess={() => showSuccess("Aufgabe erfolgreich geplant")}
          task={data}
          technicianId={Number(user!.id)}
        />
      )}

      <StartTaskModal
        show={showStart}
        setShow={setShowStart}
        taskId={data.id}
        plannedTravelDuration={data.plannedTravelDuration}
        onSuccess={() => setShowStart(false)}
      />

      <CompleteTaskModal
        show={showComplete}
        setShow={setShowComplete}
        taskId={data.id}
        plannedWorkDuration={data.plannedWorkDuration}
        plannedDepartureDuration={data.plannedDepartureDuration}
        actualStart={data.actualStart ?? null}
        onSuccess={() => {
          setShowComplete(false);
          showSuccess("Aufgabe als erledigt markiert");
        }}
      />

      <SuccessDialog
        open={dialogProps.open}
        onOpenChange={(open) => { if (!open) dialogProps.onClose(); }}
        title={dialogProps.title}
        onClose={dialogProps.onClose}
      />
    </>
  );
};
